/* BIÉDE JOURNAL progressive pagination v2026-07-25-1 */
(() => {
  const elementName = 'biede-journal-pagination';
  const stateKey = 'biedeJournalPagination';
  const stateVersion = 1;

  if (!('customElements' in window)) return;
  if (customElements.get(elementName)) return;

  class BiedeJournalPagination extends HTMLElement {
    connectedCallback() {
      if (this.dataset.biedeJournalInitialized === 'true') return;
      this.dataset.biedeJournalInitialized = 'true';

      this.currentPage = Number(this.dataset.currentPage);
      this.pageSize = Number(this.dataset.pageSize);
      this.totalItems = Number(this.dataset.totalItems);
      this.loadedCount = Number(this.dataset.initialCount);
      this.loadedPages = 0;
      this.nextUrl = this.dataset.nextUrl || '';
      this.sectionId = this.dataset.sectionId || '';
      this.grid = this.closest('.main-blog')?.querySelector('[data-biede-journal-grid]');
      this.enhancedArea = this.querySelector('[data-biede-journal-enhanced]');
      this.loadMore = this.querySelector('[data-biede-journal-load-more]');
      this.loadMoreLabel = this.querySelector('[data-biede-journal-load-more-label]');
      this.count = this.querySelector('[data-biede-journal-count]');
      this.progress = this.querySelector('[data-biede-journal-progress]');
      this.progressValue = this.querySelector('[data-biede-journal-progress-value]');
      this.complete = this.querySelector('[data-biede-journal-complete]');
      this.status = this.querySelector('[data-biede-journal-status]');
      this.loading = false;
      this.restoring = false;
      this.abortController = null;

      if (
        this.currentPage !== 1 ||
        !this.nextUrl ||
        !this.sectionId ||
        !this.grid ||
        !this.enhancedArea ||
        !this.loadMore ||
        !this.loadMoreLabel ||
        !this.count ||
        !this.progress ||
        !this.progressValue ||
        !Number.isFinite(this.pageSize) ||
        !Number.isFinite(this.totalItems) ||
        !Number.isFinite(this.loadedCount)
      ) {
        return;
      }

      try {
        const initialNextUrl = this.getPageUrl(this.nextUrl);
        this.nextUrl = `${initialNextUrl.pathname}${initialNextUrl.search}`;
      } catch (_error) {
        return;
      }

      this.onLoadMore = this.handleLoadMore.bind(this);
      this.onPageHide = this.handlePageHide.bind(this);
      this.loadMore.addEventListener('click', this.onLoadMore);
      window.addEventListener('pagehide', this.onPageHide);
      this.classList.add('is-enhanced');
      this.updateInterface();
      this.restoreExpandedState();
    }

    disconnectedCallback() {
      this.loadMore?.removeEventListener('click', this.onLoadMore);
      window.removeEventListener('pagehide', this.onPageHide);
      this.abortController?.abort();
    }

    getPageUrl(value) {
      const url = new URL(value, window.location.origin);
      const page = Number(url.searchParams.get('page'));

      url.hash = '';
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');

      if (
        url.origin !== window.location.origin ||
        url.pathname !== window.location.pathname ||
        !Number.isInteger(page) ||
        page < 2
      ) {
        throw new Error('Invalid Journal pagination URL');
      }

      return url;
    }

    getSectionUrl(pageUrl) {
      const sectionUrl = new URL(pageUrl.href);
      sectionUrl.searchParams.set('sections', this.sectionId);
      return sectionUrl;
    }

    getArticleElements(root = this.grid) {
      return Array.from(root.children).filter((element) =>
        element.matches('[data-biede-journal-article-id]')
      );
    }

    getSavedState() {
      const savedState = window.history.state?.[stateKey];
      const locationKey = `${window.location.pathname}${window.location.search}`;
      const maximumAdditionalPages = Math.ceil(this.totalItems / this.pageSize) - 1;

      if (
        !savedState ||
        savedState.version !== stateVersion ||
        savedState.locationKey !== locationKey ||
        savedState.totalItems !== this.totalItems ||
        !Number.isInteger(savedState.additionalPages) ||
        savedState.additionalPages < 1 ||
        savedState.additionalPages > maximumAdditionalPages
      ) {
        return null;
      }

      return {
        additionalPages: savedState.additionalPages,
        scrollY: Number.isFinite(savedState.scrollY) ? savedState.scrollY : 0,
      };
    }

    saveState(scrollY = window.scrollY) {
      if (this.loadedPages < 1) return;

      const currentState =
        window.history.state && typeof window.history.state === 'object'
          ? window.history.state
          : {};

      try {
        window.history.replaceState(
          {
            ...currentState,
            [stateKey]: {
              version: stateVersion,
              locationKey: `${window.location.pathname}${window.location.search}`,
              totalItems: this.totalItems,
              additionalPages: this.loadedPages,
              scrollY,
            },
          },
          '',
          window.location.href
        );
      } catch (_error) {
        // The progressive loader remains fully usable if history state is unavailable.
      }
    }

    handlePageHide() {
      this.saveState(window.scrollY);
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.toggleAttribute('aria-busy', isLoading);
      this.loadMore?.setAttribute('aria-disabled', String(isLoading));
      if (isLoading) {
        this.grid.setAttribute('aria-busy', 'true');
      } else {
        this.grid.removeAttribute('aria-busy');
      }

      if (isLoading) {
        this.loadMoreLabel.textContent = 'LOADING';
      } else {
        this.updateLoadMoreLabel();
      }
    }

    updateLoadMoreLabel() {
      if (!this.loadMoreLabel) return;
      const remainingCount = Math.max(this.totalItems - this.loadedCount, 0);
      const nextBatchCount = Math.min(this.pageSize, remainingCount);
      this.loadMoreLabel.textContent = `VIEW ${nextBatchCount} MORE`;
    }

    updateInterface() {
      const shownCount = Math.min(this.loadedCount, this.totalItems);
      const progressPercent = this.totalItems > 0 ? (shownCount / this.totalItems) * 100 : 0;

      this.count.textContent = `${String(shownCount).padStart(2, '0')} / ${this.totalItems}`;
      this.progress.setAttribute('aria-valuenow', String(shownCount));
      this.progressValue.style.setProperty('--biede-journal-progress', `${progressPercent}%`);

      if (!this.nextUrl || shownCount >= this.totalItems) {
        this.loadMore.hidden = true;
        this.complete.hidden = false;
        return;
      }

      this.loadMore.hidden = false;
      this.complete.hidden = true;
      this.loadMore.href = this.nextUrl;
      this.updateLoadMoreLabel();
    }

    prepareArticle(article) {
      const preparedArticle = document.importNode(article, true);
      preparedArticle.classList.remove(
        'scroll-trigger',
        'animate--slide-in',
        'scroll-trigger--offscreen',
        'scroll-trigger--cancel'
      );
      preparedArticle.removeAttribute('data-cascade');
      preparedArticle.style.removeProperty('--animation-order');
      return preparedArticle;
    }

    async fetchPage(value) {
      const pageUrl = this.getPageUrl(value);
      const sectionUrl = this.getSectionUrl(pageUrl);
      this.abortController = new AbortController();

      const response = await fetch(sectionUrl.href, {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Journal pagination request failed: ${response.status}`);
      }

      const responseSections = await response.json();
      const responseText = responseSections?.[this.sectionId];
      if (typeof responseText !== 'string') {
        throw new Error('Journal pagination section is missing');
      }

      const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const responseGrid = responseDocument.querySelector('[data-biede-journal-grid]');
      const responsePagination = responseDocument.querySelector(elementName);

      if (!responseGrid || !responsePagination) {
        throw new Error('Journal pagination response is incomplete');
      }

      const requestedPage = Number(pageUrl.searchParams.get('page'));
      const responsePage = Number(responsePagination.dataset.currentPage);
      const responseTotalItems = Number(responsePagination.dataset.totalItems);
      if (responsePage !== requestedPage || responseTotalItems !== this.totalItems) {
        throw new Error('Journal pagination response does not match the requested page');
      }

      const articles = this.getArticleElements(responseGrid);
      if (articles.length === 0 || articles.length > this.pageSize) {
        throw new Error('Journal pagination response has an unexpected article count');
      }

      let nextUrl = '';
      if (responsePagination.dataset.nextUrl) {
        const validatedNextUrl = this.getPageUrl(responsePagination.dataset.nextUrl);
        const nextPage = Number(validatedNextUrl.searchParams.get('page'));
        if (nextPage !== requestedPage + 1) {
          throw new Error('Journal pagination response has an invalid next page');
        }
        nextUrl = `${validatedNextUrl.pathname}${validatedNextUrl.search}`;
      }

      return {
        articles,
        nextUrl,
        pageUrl: `${pageUrl.pathname}${pageUrl.search}`,
      };
    }

    async appendPage(value, announce = true) {
      const result = await this.fetchPage(value);
      const existingArticleIds = new Set(
        this.getArticleElements().map((article) => article.dataset.biedeJournalArticleId)
      );
      let addedCount = 0;
      let firstAddedArticle = null;

      result.articles.forEach((article) => {
        const articleId = article.dataset.biedeJournalArticleId;
        if (!articleId || existingArticleIds.has(articleId)) return;

        const preparedArticle = this.prepareArticle(article);
        this.grid.appendChild(preparedArticle);
        if (!firstAddedArticle) firstAddedArticle = preparedArticle;
        existingArticleIds.add(articleId);
        addedCount += 1;
      });

      if (addedCount === 0) {
        throw new Error('Journal pagination did not add any new articles');
      }

      this.loadedCount = this.getArticleElements().length;
      this.loadedPages += 1;
      this.nextUrl = result.nextUrl;
      this.updateInterface();

      if (announce && this.status) {
        this.status.textContent = `${addedCount} more Journal articles loaded. ${this.loadedCount} of ${this.totalItems} shown.`;
      }

      return {
        pageUrl: result.pageUrl,
        firstAddedArticle,
      };
    }

    async handleLoadMore(event) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if (this.loading || !this.nextUrl) {
        event.preventDefault();
        return;
      }

      const fallbackUrl = this.getPageUrl(this.nextUrl);
      const keyboardActivation = event.detail === 0;
      event.preventDefault();
      this.setLoading(true);

      try {
        const result = await this.appendPage(fallbackUrl.href);
        this.saveState(window.scrollY);
        if (keyboardActivation) {
          result.firstAddedArticle?.querySelector('a[href]')?.focus();
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        window.location.assign(fallbackUrl.href);
      } finally {
        this.setLoading(false);
      }
    }

    async restoreExpandedState() {
      const savedState = this.getSavedState();
      if (!savedState) return;

      this.restoring = true;
      this.setLoading(true);

      try {
        for (let index = 0; index < savedState.additionalPages; index += 1) {
          if (!this.nextUrl) break;
          await this.appendPage(this.nextUrl, false);
        }

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.scrollTo({ top: savedState.scrollY, behavior: 'auto' });
          });
        });
      } catch (_error) {
        // Keep the native first page usable if an old expanded state cannot be rebuilt.
      } finally {
        this.restoring = false;
        this.setLoading(false);
        this.saveState(savedState.scrollY);
      }
    }
  }

  customElements.define(elementName, BiedeJournalPagination);
})();
